import React from "react";
import { Header } from "@/components/Header";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, Newspaper, Calendar, ArrowRight } from "lucide-react";

const MediaPage = () => {
  const pressReleases = [
    {
      title: "SKI Asia-Pacific Launches in Dubai to Support Digital-Era Governance Frameworks",
      source: "Khaleej Times",
      date: "December 30, 2025",
      excerpt: "SKI Asia-Pacific (SKI APAC – FZCO) has officially launched in Dubai, introducing a governance-focused platform aimed at supporting institutions navigating the demands of the digital economy. Operating from Dubai Digital Park, the organisation positions itself as a governance innovation company serving the Gulf, Asia-Pacific, Africa, and Europe.",
      url: "https://www.khaleejtimes.com/kt-network/ski-asia-pacific-launches-in-dubai-to-support-digital-era-governance-frameworks",
      featured: true,
      image: "https://imgengine.khaleejtimes.com/khaleejtimes-english/2025-12-30/9lp3hpga/Picture1.jpg?width=600&height=400&format=auto"
    }
  ];

  const keyHighlights = [
    "Introduction of the Three-Layer Governance Model for digital-era institutions",
    "Launch of the Governance Desk - an integrated governance environment",
    "90-day Governance Transformation Pilot for Gulf-based institutions",
    "Operations spanning Europe, Asia-Pacific, Africa, and the Middle East",
    "Focus on compliance, collaboration, and cross-border institutional resilience"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-green-50">
      <Header />

      {/* Hero */}
      <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white py-16">
        <div className="container mx-auto px-6 text-center">
          <Badge className="bg-white/20 text-white mb-4">Press & Media</Badge>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">In The News</h1>
          <p className="text-xl opacity-90 max-w-2xl mx-auto">
            Latest coverage and announcements about SKI Asia Pacific's governance innovation initiatives
          </p>
        </div>
      </div>

      <div className="container mx-auto px-6 py-12">
        {/* Featured Article */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <Newspaper className="h-6 w-6 text-green-600" />
            Featured Coverage
          </h2>
          
          {pressReleases.filter(p => p.featured).map((article, idx) => (
            <Card key={idx} className="overflow-hidden hover:shadow-xl transition-shadow">
              <div className="md:flex">
                <div className="md:w-1/3">
                  <img 
                    src={article.image} 
                    alt={article.title}
                    className="w-full h-64 md:h-full object-cover"
                  />
                </div>
                <CardContent className="md:w-2/3 p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Badge variant="secondary">{article.source}</Badge>
                    <span className="text-sm text-gray-500 flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      {article.date}
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-gray-900">{article.title}</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">{article.excerpt}</p>
                  
                  <div className="mb-6">
                    <h4 className="font-semibold mb-3">Key Highlights:</h4>
                    <ul className="space-y-2">
                      {keyHighlights.map((highlight, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                          <ArrowRight className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                          {highlight}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Button 
                    onClick={() => window.open(article.url, '_blank')}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    Read Full Article <ExternalLink className="h-4 w-4 ml-2" />
                  </Button>
                </CardContent>
              </div>
            </Card>
          ))}
        </div>

        {/* Article Quote */}
        <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200 mb-12">
          <CardContent className="p-8">
            <blockquote className="text-xl italic text-gray-700 border-l-4 border-green-500 pl-6">
              "SKI Asia-Pacific's launch aligns with ongoing economic transformation initiatives across the Gulf region, where national strategies increasingly prioritise digitalisation, sustainability, talent integration, and global competitiveness."
            </blockquote>
            <p className="mt-4 text-gray-500">— Khaleej Times, December 2025</p>
          </CardContent>
        </Card>

        {/* Three-Layer Model from Article */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6">The Three-Layer Governance Model</h2>
          <p className="text-gray-600 mb-6">As featured in Khaleej Times coverage:</p>
          
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="border-t-4 border-t-blue-500">
              <CardContent className="p-6">
                <h3 className="font-bold text-lg mb-3">Layer 1: Compliance & Control</h3>
                <p className="text-gray-600 text-sm">
                  Focus on regulatory alignment, operational clarity, and risk management to ensure institutions meet local and international standards.
                </p>
              </CardContent>
            </Card>
            
            <Card className="border-t-4 border-t-green-500">
              <CardContent className="p-6">
                <h3 className="font-bold text-lg mb-3">Layer 2: Post-Agreement Assurance</h3>
                <p className="text-gray-600 text-sm">
                  Designed to support the execution of partnerships, MoUs, investments, and mandates through performance monitoring and milestone tracking.
                </p>
              </CardContent>
            </Card>
            
            <Card className="border-t-4 border-t-purple-500">
              <CardContent className="p-6">
                <h3 className="font-bold text-lg mb-3">Layer 3: Collaboration & Expansion</h3>
                <p className="text-gray-600 text-sm">
                  Support ecosystem development by enabling institutions to identify collaboration opportunities and plan regional or international expansion.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Contact for Media */}
        <Card className="bg-gray-900 text-white">
          <CardContent className="p-8 text-center">
            <h3 className="text-2xl font-bold mb-4">Media Inquiries</h3>
            <p className="opacity-80 mb-6">For press releases, interviews, or media partnerships:</p>
            <div className="space-y-2">
              <p><strong>Email:</strong> neha.dewan@skiasia.org</p>
              <p><strong>Phone:</strong> +971 52 965 0406</p>
            </div>
            <Button 
              variant="outline" 
              className="mt-6 border-white text-white hover:bg-white hover:text-gray-900"
              onClick={() => window.open('https://www.skiasiapacific.com', '_blank')}
            >
              Visit SKI Asia Pacific <ExternalLink className="h-4 w-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MediaPage;
